import { DocumentViewer } from '@aecom-ai-engineering/react-components';
import { Spinner } from '@consigli/facade';
import {
  useProjectId,
  usePackageId,
  useDocumentPackageResults,
  useGetFileReadUrlQuery,
} from '@consigli/hooks';
import { DocumentPackageResult, StatusVariant, File as ConsigliFile } from '@consigli/types';
import { ColumnDef } from '@tanstack/react-table';
import { FC, useMemo, useState } from 'react';
import { BsThreeDots } from 'react-icons/bs';

import {
  ActionButton,
  Badge,
  DataTable,
  ObjectDetailView,
  createBackofficeRenderers,
} from '@/components';
import { isValidDocumentType, resolveViewerFileType } from '@/utils/document-check';

const downloadReportFile = async (file: DocumentPackageResult['file']): Promise<void> => {
  try {
    if (!file.fileUrl) {
      throw new Error('File URL is not available');
    }
    const response = await fetch(file.fileUrl);
    if (!response.ok) {
      throw new Error('Network response was not ok');
    }
    const fileBlob = await response.blob();

    const url = window.URL.createObjectURL(fileBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = file.originalFilename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error: unknown) {
    console.error('Error downloading file:', error);
  }
};

interface ActionCellProps {
  item: DocumentPackageResult;
  onViewDetails: (item: DocumentPackageResult) => void;
  onPreview: (item: DocumentPackageResult) => void;
}

const ActionCell = ({ item, onViewDetails, onPreview }: ActionCellProps) => {
  const filename = item.file.originalFilename;
  const canPreview = isValidDocumentType(filename);

  const actions = [
    ...(canPreview
      ? [
          {
            label: 'Show',
            onClick: () => onPreview(item),
          },
        ]
      : []),
    {
      label: 'View Details',
      onClick: () => onViewDetails(item),
    },
    {
      label: 'Download',
      onClick: () => downloadReportFile(item.file),
    },
  ];

  return <ActionButton icon={<BsThreeDots />} actions={actions} />;
};

const ReportFileViewer: FC<{
  projectId: number;
  file: ConsigliFile;
  onClose: () => void;
}> = ({ projectId, file, onClose }) => {
  const { data, isLoading } = useGetFileReadUrlQuery({ projectId, fileId: file.id });
  const renderers = useMemo(() => createBackofficeRenderers(), []);

  if (!data?.readUrl) {
    if (isLoading) {
      return (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/20"
          role="presentation"
        >
          <Spinner />
        </div>
      );
    }
    return null;
  }

  return (
    <DocumentViewer
      open
      onOpenChange={(details) => {
        if (!details.open) onClose();
      }}
      src={data.readUrl}
      title={file.originalFilename}
      fileName={file.originalFilename}
      fileType={resolveViewerFileType(file.originalFilename)}
      showSidebar={false}
      renderers={renderers}
    />
  );
};

export const ReportsTab = () => {
  const projectId = useProjectId();
  const packageId = usePackageId();

  const [selectedItem, setSelectedItem] = useState<DocumentPackageResult | null>(null);
  const [isDetailViewOpen, setIsDetailViewOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState<DocumentPackageResult['file'] | null>(null);

  const { results, isLoading } = useDocumentPackageResults({
    projectId,
    documentPackageId: packageId,
    page: 'all',
  });

  const handleViewDetails = (item: DocumentPackageResult) => {
    setSelectedItem(item);
    setIsDetailViewOpen(true);
  };

  const handlePreview = (item: DocumentPackageResult) => {
    setPreviewFile(item.file);
  };

  const columns: ColumnDef<DocumentPackageResult>[] = useMemo(
    () => [
      {
        id: 'filename',
        header: 'Filename',
        accessorFn: (row) => row.file.originalFilename,
        cell: ({ row }) => (
          <span className="font-medium text-gray-900">{row.original.file.originalFilename}</span>
        ),
      },
      {
        id: 'type',
        header: 'Type',
        accessorFn: (row) => row.type,
        cell: ({ row }) => <Badge variant={StatusVariant.INFO} text={row.original.type} />,
      },
      {
        id: 'createdAt',
        header: 'Created',
        accessorFn: (row) => row.createdAt,
        cell: ({ row }) => (
          <span className="text-gray-600">
            {new Date(row.original.createdAt).toLocaleDateString()}
          </span>
        ),
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => (
          <ActionCell
            item={row.original}
            onViewDetails={handleViewDetails}
            onPreview={handlePreview}
          />
        ),
      },
    ],
    [],
  );

  return (
    <>
      <div className="w-full">
        {results.length === 0 && !isLoading ? (
          <div className="flex justify-center items-center h-40 text-gray-500">
            No reports generated yet
          </div>
        ) : (
          <DataTable<DocumentPackageResult, unknown>
            columns={columns as ColumnDef<DocumentPackageResult, unknown>[]}
            data={results}
            isLoading={isLoading}
            scrollable
            height="60vh"
          />
        )}
      </div>

      {selectedItem && (
        <ObjectDetailView
          object={selectedItem}
          objectType="Report"
          summary={`Report "${selectedItem.file.originalFilename}" details.`}
          isOpen={isDetailViewOpen}
          onOpenChange={(open: boolean) => {
            setIsDetailViewOpen(open);
            if (!open) {
              setSelectedItem(null);
            }
          }}
        />
      )}

      {previewFile && (
        <ReportFileViewer
          projectId={projectId}
          file={previewFile}
          onClose={() => setPreviewFile(null)}
        />
      )}
    </>
  );
};
